/**
 * Debounce function
 * @param {Function} func - The function to debounce
 * @param {boolean} immediate - Whether to execute the function immediately
 * @returns {Function} Debounced function
 */

export function debounce(func, immediate) {
  let timeout
  return function executedFunction() {
    const context = this
    const args = arguments

    const later = () => {
      timeout = null
      if (!immediate) func.apply(context, args)
    }

    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, 500)
    if (callNow) func.apply(context, args)
  }
}

/**
 * appendParams function
 * A function for appending query parameters to the current URL and updating the browser history
 * @param {Object} value - The object containing the parameters to be appended
 */
export function appendParams(value) {
  const params = parseParams(window.location.search)
  const data = { ...params, ...value }

  // Remove empty or invalid values
  Object.keys(data).forEach((key) => {
    if (data[key] === '' || !data[key]?.toString().length) {
      delete data[key]
    }
  })

  // Exclude `start=0` if `search` is cleared
  if (!data.search && data.start === 0) {
    delete data.start
  }

  const queryString = new URLSearchParams(data).toString()
  const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
  window.history.pushState({}, null, newUrl)
}

/**
 * parseParams function
 * A function for parsing query parameters from a given URL string or the current location
 * @param {string} params - The URL string containing the query parameters (default: '')
 * @returns {Object} An object containing the parsed query parameters
 */
export function parseParams(queryString = '') {
  const params = new URLSearchParams(queryString)
  const result = {}

  for (const [key, value] of params.entries()) {
    // Check if the value is supposed to be an array
    if (value.includes(',')) {
      result[key] = value.split(',') // Convert to array
    } else if (!isNaN(value)) {
      // Check if the value can be parsed as an integer
      result[key] = parseInt(value, 10) // Convert to integer
    } else if (value === 'true' || value === 'false') {
      // Check if the value is a boolean
      result[key] = value === 'true' // Convert to boolean
    } else {
      // Otherwise, keep it as a string
      result[key] = value // Keep as string
    }
  }

  return result
}

/**
 * setSortType function
 * @param {Array} data - The array of objects to set sort types for
 * @param {Object} payload - The payload containing the sort information
 * @param {string} payload.sSortBy - The internal name of the column to sort by
 * @param {number} payload.nOrder - The order (ascending or descending) of the sorting
 * @returns {Array} Updated array of objects with sort types set
 */
export function setSortType(data, payload) {
  return data.map((data) => {
    if (data.sInternalName === payload?.sSortBy) data.type = Number(payload?.nOrder)
    else data.type = 0
    return data
  })
}

/**
 * convertObjectToQueryString function
 * A function for converting an object into a query string and a filtered object
 * @param {Object} obj - The object to be converted into a query string
 * @returns {Object} An object containing the query string and the filtered object
 */
export function convertObjectToQueryString(obj) {
  const filteredParams = Object.fromEntries(
    Object.entries(obj).filter(([, value]) => (value && value !== undefined) || value === 0 || typeof value === 'boolean')
  )
  return { queryString: new URLSearchParams(filteredParams).toString(), queryObject: filteredParams }
}

/**
 * formatDate function
 * A function for formatting a date string according to the provided format
 * @param {string} dateString - The date string to be formatted
 * @param {'YYYY-MM-DD' | 'YYYY/MM/DD' | 'YYYY-M-D' | 'YYYY/M/D' |
 *         'YY-MM-DD' | 'YY/MM/DD' | 'YY-M-D' | 'YY/M/D' |
 *         'MMMM D, YYYY' | 'MMMM D, YY' |
 *         'MM/DD/YYYY' | 'MM-DD-YYYY' | 'M/D/YYYY' | 'M-D-YYYY' |
 *         'MM/DD/YY' | 'MM-DD-YY' | 'M/D/YY' | 'M-D-YY' |
 *         'DD/MM/YYYY' | 'DD-MM-YYYY' | 'D/M/YYYY' | 'D-M-YYYY' |
 *         'DD/MM/YY' | 'DD-MM-YY' | 'D/M/YY' | 'D-M-YY' |
 *         'YYYY-MM-DD HH:mm:ss' | 'YYYY/MM/DD HH:mm:ss' |
 *         'YYYY-M-D HH:mm:ss' | 'YYYY/M/D HH:mm:ss' |
 *         'YY-MM-DD HH:mm:ss' | 'YY/MM/DD HH:mm:ss' |
 *         'YY-M-D HH:mm:ss' | 'YY/M/D HH:mm:ss' |
 *         'MM/DD/YYYY HH:mm:ss' | 'MM-DD-YYYY HH:mm:ss' |
 *         'M/D/YYYY HH:mm:ss' | 'M-D-YYYY HH:mm:ss' |
 *         'MM/DD/YY HH:mm:ss' | 'MM-DD-YY HH:mm:ss' |
 *         'M/D/YY HH:mm:ss' | 'M-D-YY HH:mm:ss' |
 *         'DD/MM/YYYY HH:mm:ss' | 'DD-MM-YYYY HH:mm:ss' |
 *         'D/M/YYYY HH:mm:ss' | 'D-M-YYYY HH:mm:ss' |
 *         'DD/MM/YY HH:mm:ss' | 'DD-MM-YY HH:mm:ss' |
 *         'D/M/YY HH:mm:ss' | 'D-M-YY HH:mm:ss'} format - The format string specifying the desired date format
 * @returns {string} The formatted date string
 */
export function formatDate(dateString, format = '') {
  if (!dateString) return null
  const date = new Date(dateString)
  const formatOptions = {
    YYYY: date.getFullYear(), // 2021
    YY: String(date.getFullYear()).slice(-2), // 21
    MMMM: new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date), // January
    MMM: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date), // Jan
    EEE: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()], // Mon
    MM: String(date.getMonth() + 1).padStart(2, '0'), // 01
    M: date.getMonth() + 1, // 1
    DD: String(date.getDate()).padStart(2, '0'), // 01
    D: date.getDate(), // 1
    HH: String(date.getHours()).padStart(2, '0'), // 15
    hh: String(date.getHours() % 12 || 12).padStart(2, '0'), // 07 or 12
    mm: String(date.getMinutes()).padStart(2, '0'), // 01
    ss: String(date.getSeconds()).padStart(2, '0'), // 01
    A: date.getHours() >= 12 ? 'PM' : 'AM'
  }

  return format.replace(/YYYY|YY|MMMM|MMM|EEE|MM|M|DD|DO|D|HH|hh|mm|ss|A/g, (match) => formatOptions[match])
}
/**
 * emitEvent function
 * A function for emitting a custom event on the given element
 * @param {string} type - The type of the event
 * @param {Object} [detail={}] - The detail object of the event
 * @param {HTMLElement} [elem=document] - The element on which to emit the event
 * @returns {boolean} Whether the event was cancelled or not
 */
export function emitEvent(type, detail = {}, elem = document) {
  if (!type) return

  const event = new CustomEvent(type, {
    bubbles: true,
    cancelable: true,
    detail
  })

  return elem.dispatchEvent(event)
}
/**
 * navigationTo function
 * A function for navigating to the given link
 * @param {string} link - The link to navigate to
 */

export function navigationTo(link) {
  emitEvent('navigateTo', link)
}

/**
 * removeToken function
 * A function for removing the token from localStorage
 * @returns {void} - No return value
 */

export function removeToken() {
  localStorage.removeItem('token')
}

export function convertInDateFormat(date) {
  const parsedDate = new Date(date)

  return parsedDate
}
export function convertISOFormat(date) {
  if (!date) {
    return '' // Return an empty string if the date is undefined, null, or falsy
  }

  const parsedDate = new Date(date).toISOString()
  return parsedDate
}

export function convertCapitalize(text) {
  if (typeof text !== 'string' || !text.trim()) return text // Validate input

  const trimmedText = text.trim() // Remove leading/trailing spaces
  const result = trimmedText.charAt(0).toUpperCase() + trimmedText.slice(1).toLowerCase() // Capitalize first char, lowercase the rest

  return result
}

// Set item in storage.
export const setStorage = (type, key, value, options) => {
  try {
    if (typeof window === 'undefined') {
      console.warn(`Cannot set storage item "${key}" on the server.`)
      return
    }

    const stringValue = JSON.stringify(value)
    if (type === 1) {
      localStorage.setItem(key, stringValue)
    } else if (type === 2) {
      sessionStorage.setItem(key, stringValue)
    }
  } catch (error) {
    console.error(`Error setting storage item "${key}":`, error)
  }
}

// Get storage item
export const getStorage = (type, key) => {
  try {
    if (typeof window === 'undefined' && type !== 3) {
      console.warn(`Cannot get storage item "${key}" on the server.`)
      return null
    }

    let item = null

    if (type === 1) {
      item = localStorage.getItem(key)
    } else if (type === 2) {
      item = sessionStorage.getItem(key)
    }

    return item ? JSON.parse(item) : null
  } catch (error) {
    console.error(`Error getting storage item "${key}":`, error)
    return null
  }
}

// Clear storage item
export const clearStorage = (type, key) => {
  try {
    if (typeof window === 'undefined' && type !== 3) {
      console.warn(`Cannot clear storage item "${key}" on the server.`)
      return
    }

    if (type === 1) {
      localStorage.removeItem(key)
    } else if (type === 2) {
      sessionStorage.removeItem(key)
    }
  } catch (error) {
    console.error(`Error clearing storage item "${key}":`, error)
  }
}

// is Bottom reached
export function isBottomReached(id, callBack) {
  const controller = new AbortController()
  function handleScroll() {
    const ele = document.getElementById(id)
    if (ele) {
      if (ele.offsetTop <= window.scrollY + window.innerHeight) {
        callBack(ele.offsetTop <= window.scrollY + window.innerHeight)
        controller.abort()
      }
    }
  }
  window.addEventListener('scroll', handleScroll, { signal: controller.signal })
}

export const calculateAge = (birthDate) => {
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const isBirthdayPassed =
    today.getMonth() > birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate())

  if (!isBirthdayPassed) {
    age--
  }

  return age
}
